<template>
  <div class="share-list">
    <template v-if="shares.length > 0">
      <h4 class="mt-0 mb-2">
        {{ $t("headings.modelSharedWith") }}
      </h4>
      <table v-if="shares.length > 0" class="share-list__list">
        <thead>
          <tr>
            <th>{{ $t("labels.name") }}</th>
            <th>{{ $t("labels.email") }}</th>
            <th>{{ $t("labels.canCopy") }}</th>
            <th>{{ $t("actions.deleteShare") }}</th>
          </tr>
        </thead>
        <transition-group tag="tbody" name="share-list__listanim">
          <tr v-for="share in shares" :key="share.id" class="share-list__item">
            <td>
              <div class="share-list__username">
                {{ share.user?.name || $t("labels.unknownUser") }}
              </div>
            </td>
            <td>
              {{ share.user.email }}
            </td>
            <td>
              <input-switch
                :value="share.canFork"
                :text-on="$t('labels.canCopy')"
                :text-off="$t('labels.cantCopy')"
                @update:value="updateCanFork(share, $event)"
              ></input-switch>
            </td>
            <td>
              <button
                :title="$t('actions.deleteShare')"
                class="button-icon"
                type="button"
                @click="deleteShare(share)"
              >
                <lucide-icon name="trash-2" />
              </button>
            </td>
          </tr>
        </transition-group>
      </table>
    </template>

    <button
      type="button"
      class="button mt-2 button_secondary"
      :disabled="!canShare"
      @click="shareModel"
    >
      {{ $t("actions.shareModel") }}
      <limits-badge
        v-if="!canShare"
        type="canShare"
        style="right: -0.5rem"
      />
    </button>

    <workspace-model-share-popup
      :visible="sharePopupOpen"
      :model="model"
      @close="sharePopupOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { showError } from "@core/ui/alert/alert";

import LucideIcon from "@core/ui/LucideIcon.vue";
import InputSwitch from "@core/input/InputSwitch.vue";

import LimitsBadge from "@core/ui/LimitsBadge.vue";
import WorkspaceModelSharePopup from "../workspacemodels/ModelSharePopup.vue";

import { useModels } from "@/api/models.store";
import type { Model } from "@/api/Model.ts";
import type { ModelShare } from "@/api/ModelShare.ts";

const { model, canShare = true } = defineProps<{
  model: Model;
  canShare?: boolean;
}>();

const { t } = useI18n();
const { destroyShare, updateShare } = useModels();

const sharePopupOpen = ref(false);

const shares = computed(() => model.shares ?? []);

function shareModel() {
  sharePopupOpen.value = true;
}
async function deleteShare(share: { id: number }) {
  const modelId = model.id;
  if (typeof modelId !== "number") {
    showError(t("errors.modelNotSynchronized"))
    return;
  }
  try {
    destroyShare(modelId, share.id);
  } catch (error) {
    console.log(error);
  }
}

function updateCanFork(share: ModelShare, canFork: boolean) {
  const oldValue = share.canFork;
  const modelId = model.id;
  if (typeof modelId !== "number") {
    showError(t("errors.modelNotSynchronized"))
    return;
  }
  try {
    updateShare(modelId, {
      id: share.id,
      canFork: canFork
    });
  } catch (error) {
    share.canFork = oldValue;
  }
}
</script>

<style lang="scss">
.share-list {
  &__list {
    width: 100%;
    display: block;

    & > thead,
    & > tbody {
      display: block;

      & > tr {
        display: grid;
        grid-template-columns: 1fr 1fr 10rem 4rem;
        align-items: center;
        gap: 0.5rem;

        th,
        td {
          display: flex;
          align-items: center;
          justify-content: stretch;
        }
      }
    }

    & > thead {
      height: 0;
      font-size: 0;

      & > tr {
        th {
          text-align: left;
          color: var(--color-text-light);
          font-weight: 400;
          pointer-events: none;
        }
      }
    }

    button {
      margin: 0 auto;
    }
  }

  &__item {
    padding: 0.25rem 1rem;
    background-color: var(--color-background-close);
    box-shadow: 0 1px 3px var(--color-shadow-1);

    & + & {
      margin-top: 0.5rem;
    }
  }

  &__username {
    font-weight: 600;
  }

  &__listanim {
    &-enter-active,
    &-leave-active {
      transition: all 400ms ease-out;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
}
</style>
